// src/services/boardService.ts - 掲示板機能のFirebase操作（設定機能追加版）

import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  updateDoc,
  doc,
  increment,
  where,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { db } from './firebase';
import { getCurrentUserId } from './auth';

const getUserId = (): string => {
  return getCurrentUserId();
};

// ========== 型定義 ==========

export type ThreadCategory = 'chat' | 'question' | 'region' | 'tips' | 'announce';

export interface BoardSettings {
  userName: string;
  region: string;
  defaultAnonymous: boolean;
}

export interface Thread {
  id?: string;
  userId: string;
  userName: string;
  region: string;
  platform: string;
  category: ThreadCategory;
  title: string;
  content: string;
  likeCount: number;
  commentCount: number;
  viewCount: number;
  isAnonymous: boolean;
  isPinned: boolean;
  createdAt: number;
}

export interface Comment {
  id?: string;
  threadId: string;
  userId: string;
  userName: string;
  region: string;
  content: string;
  likeCount: number;
  createdAt: number;
}

export interface ThreadLike {
  userId: string;
  threadId: string;
  createdAt: number;
}

export interface CommentLike {
  userId: string;
  commentId: string;
  createdAt: number;
}

// ========== 掲示板設定 ==========

export const getBoardSettings = async (): Promise<BoardSettings> => {
  try {
    const userId = getUserId();
    const docRef = doc(db, `users/${userId}/settings`, 'board');
    const snapshot = await getDoc(docRef);
    
    if (snapshot.exists()) {
      return snapshot.data() as BoardSettings;
    }
    
    return { 
      userName: '', 
      region: '',
      defaultAnonymous: false
    };
  } catch (error) {
    console.error('掲示板設定取得エラー:', error);
    return { 
      userName: '', 
      region: '',
      defaultAnonymous: false
    };
  }
};

export const setBoardSettings = async (settings: BoardSettings): Promise<void> => {
  try {
    const userId = getUserId();
    const docRef = doc(db, `users/${userId}/settings`, 'board');
    await setDoc(docRef, settings, { merge: true });
  } catch (error) {
    console.error('掲示板設定保存エラー:', error);
    throw error;
  }
};

// ========== スレッド操作 ==========

export const createThread = async (
  thread: Omit<Thread, 'id' | 'userId' | 'userName' | 'region' | 'platform' | 'likeCount' | 'commentCount' | 'viewCount'>
): Promise<string> => {
  try {
    const userId = getUserId();
    const settings = await getBoardSettings();
    
    const threadData: Omit<Thread, 'id'> = {
      ...thread,
      userId,
      userName: thread.isAnonymous ? '匿名' : settings.userName || '匿名',
      region: settings.region || '未設定',
      platform: '',
      likeCount: 0,
      commentCount: 0,
      viewCount: 0
    };
    
    const docRef = await addDoc(collection(db, 'threads'), threadData);
    return docRef.id;
  } catch (error) {
    console.error('スレッド作成エラー:', error);
    throw error;
  }
};

export const getThreads = async (): Promise<Thread[]> => {
  try {
    const q = query(
      collection(db, 'threads'),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Thread));
  } catch (error) {
    console.error('スレッド取得エラー:', error);
    return [];
  }
};

export const getThreadById = async (threadId: string): Promise<Thread | null> => {
  try {
    const docRef = doc(db, 'threads', threadId);
    const snapshot = await getDoc(docRef);
    
    if (snapshot.exists()) {
      // 閲覧数を増やす
      await updateDoc(docRef, {
        viewCount: increment(1)
      });
      
      return { id: snapshot.id, ...snapshot.data() } as Thread;
    }
    
    return null;
  } catch (error) {
    console.error('スレッド取得エラー:', error);
    return null;
  }
};

// ========== コメント操作 ==========

export const createComment = async (
  comment: Omit<Comment, 'id' | 'userId' | 'userName' | 'region' | 'likeCount' | 'createdAt'>
): Promise<string> => {
  try {
    const userId = getUserId();
    const settings = await getBoardSettings();
    
    const commentData: Omit<Comment, 'id'> = {
      ...comment,
      userId,
      userName: settings.userName || '匿名',
      region: settings.region || '未設定',
      likeCount: 0,
      createdAt: Date.now()
    };
    
    const docRef = await addDoc(collection(db, 'comments'), commentData);
    
    // スレッドのコメント数を増やす
    const threadRef = doc(db, 'threads', comment.threadId);
    await updateDoc(threadRef, {
      commentCount: increment(1)
    });
    
    return docRef.id;
  } catch (error) {
    console.error('コメント作成エラー:', error);
    throw error;
  }
};

export const getComments = async (threadId: string): Promise<Comment[]> => {
  try {
    const q = query(
      collection(db, 'comments'),
      where('threadId', '==', threadId),
      orderBy('createdAt', 'asc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Comment));
  } catch (error) {
    console.error('コメント取得エラー:', error);
    return [];
  }
};

// ========== いいね機能 ==========

export const likeThread = async (threadId: string): Promise<void> => {
  try {
    const userId = getUserId();
    
    // 既にいいね済みかチェック
    const likesQuery = query(
      collection(db, 'threadLikes'),
      where('userId', '==', userId),
      where('threadId', '==', threadId)
    );
    
    const likesSnapshot = await getDocs(likesQuery);
    
    if (!likesSnapshot.empty) {
      throw new Error('既にいいね済みです');
    }
    
    // いいねを記録
    await addDoc(collection(db, 'threadLikes'), {
      userId,
      threadId,
      createdAt: Date.now()
    });
    
    // スレッドのいいね数を増やす
    const threadRef = doc(db, 'threads', threadId);
    await updateDoc(threadRef, {
      likeCount: increment(1)
    });
  } catch (error) {
    console.error('いいねエラー:', error);
    throw error;
  }
};

export const likeComment = async (commentId: string): Promise<void> => {
  try {
    const userId = getUserId();
    
    // 既にいいね済みかチェック
    const likesQuery = query(
      collection(db, 'commentLikes'),
      where('userId', '==', userId),
      where('commentId', '==', commentId)
    );
    
    const likesSnapshot = await getDocs(likesQuery);
    
    if (!likesSnapshot.empty) {
      throw new Error('既にいいね済みです');
    }
    
    // いいねを記録
    await addDoc(collection(db, 'commentLikes'), {
      userId,
      commentId,
      createdAt: Date.now()
    });
    
    // コメントのいいね数を増やす
    const commentRef = doc(db, 'comments', commentId);
    await updateDoc(commentRef, {
      likeCount: increment(1)
    });
  } catch (error) {
    console.error('いいねエラー:', error);
    throw error;
  }
};

export const hasLikedThread = async (threadId: string): Promise<boolean> => {
  try {
    const userId = getUserId();
    
    const q = query(
      collection(db, 'threadLikes'),
      where('userId', '==', userId),
      where('threadId', '==', threadId)
    );
    
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('いいね確認エラー:', error);
    return false;
  }
};

export const hasLikedComment = async (commentId: string): Promise<boolean> => {
  try {
    const userId = getUserId();
    
    const q = query(
      collection(db, 'commentLikes'),
      where('userId', '==', userId),
      where('commentId', '==', commentId)
    );
    
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('いいね確認エラー:', error);
    return false;
  }
};